import React, { useState } from 'react';
import { Image, Linking, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Modal from 'react-native-modal';

import Colors from '../../constants/Colors';
import Layout from '../../constants/Layout';
import { Pantry, ServiceType, User, Visit } from '../../types';
import tefap from '../../utils/tefap';
import timeUtils from '../../utils/timeUtils';
import FilledButton from './../FilledButton';

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  modal: {
    alignItems: 'center',
  },
  contentContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    marginVertical: 'auto',
    backgroundColor: Colors.white,
    width: Layout.fullWidth > 600 ? 585 : Layout.fullWidth - 32,
    maxHeight: 550,
    borderRadius: 4,
  },
  body: {
    alignItems: 'center',
    paddingTop: 10,
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  modalImage: {
    width: 95,
    height: 69,
    marginBottom: 31,
    marginTop: 34,
    marginRight: 'auto',
    marginLeft: 'auto',
  },
  headerText: {
    fontSize: 24,
    fontWeight: '600',
    marginBottom: 25,
  },
  boldText: {
    fontWeight: 'bold',
  },
  subTitle: {
    marginBottom: 8,
  },
  tefapContainer: {
    marginTop: 16,
    width: Layout.fullWidth > 600 ? 490 : Layout.fullWidth - 90,
    alignItems: 'center',
  },
  tefapText: {
    fontSize: 16,
    textAlign: 'center',
  },
  tefapDescriptionText: {
    fontSize: 16,
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 16,
  },
  tefapInfoLink: {
    color: Colors.darkGreen,
    textTransform: 'uppercase',
    fontWeight: '600',
    borderBottomColor: Colors.darkGreen,
    borderBottomWidth: 1,
  },
  buttonRow: {
    height: 72,
    backgroundColor: Colors.lightGrayBackground,
    borderBottomRightRadius: 4,
    borderBottomLeftRadius: 4,
    borderTopWidth: 1,
    borderTopColor: Colors.grayBorder,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonContainer: {
    width: Layout.fullWidth > 600 ? 260 : Layout.fullWidth / 2 - 30,
    marginHorizontal: 8,
  },
});

type Props = {
  translation: { [key: string]: string };
  closeModal: () => void;
  cancelModal: () => void;
  showTefapAttestationModal: () => void;
  showMissingInfoModal: () => void;
  locale: string;
  user: User;
  // TODO: define registration type properly
  registration: any;
  hasMissingInfo: boolean;
  pantry: Pantry;
  visit: Visit;
  organizationId: number;
};

function ReservationConfirmedModal(props: Props) {
  const [hideTefap, setHideTefap] = useState(false);

  const {
    translation,
    closeModal,
    cancelModal,
    showTefapAttestationModal,
    showMissingInfoModal,
    locale,
    user,
    registration,
    hasMissingInfo,
    pantry,
    visit,
    organizationId,
  } = props;

  const tefapLink = 'https://www.fns.usda.gov/tefap/tefap-fact-sheet';
  const niceDate = timeUtils.getNiceDate(visit.service_date, locale, translation.app_today);
  const niceTime = timeUtils.timeSinceMidnightToString(visit.time);
  const niceUntilTime = timeUtils.timeSinceMidnightToString(visit.until_time);

  const openTefapLink = () => {
    if (Platform.OS === 'web') {
      window.open(tefapLink, '_blank');
    } else {
      Linking.openURL(tefapLink);
    }
  };

  const onCancel = () => {
    // let caller know that we're not opening a new modal
    cancelModal();
  };

  const handleReservationCompleteFlow = (screenAction: () => void): void => {
    // request close from caller but with different callback to signify that we're opening a new modal
    closeModal();

    // show next screen modal only after previous closes
    setTimeout(() => {
      screenAction();
    }, 300); // Allow modal close animation to complete
  };

  let needsTefap = false;
  const tefapStatus = tefap.getTefapStatus(user);
  const showTefapIf = ['expired', 'incomplete', 'not_signed'];
  if (showTefapIf.indexOf(tefapStatus) !== -1) {
    needsTefap = true;
  } else if (!visit.tefap_attestation) {
    needsTefap = true;
  }

  let extraBodyContent = null;
  const buttonRowContent = [];
  if (pantry.use_tefap && needsTefap && !hideTefap) {
    buttonRowContent.push(
      <View style={styles.buttonContainer} key="tefap_later">
        <FilledButton
          onPress={() => {
            setHideTefap(true);
          }}
          label={translation.program_complete_later}
          full={false}
          color={Colors.darkGreen}
          paddingHorizontal={16}
        />
      </View>
    );
    buttonRowContent.push(
      <View style={styles.buttonContainer} key="tefap_now">
        <FilledButton
          onPress={() => handleReservationCompleteFlow(() => showTefapAttestationModal())}
          label={translation.program_complete_now}
          full={true}
          color={Colors.darkGreen}
          paddingHorizontal={16}
          testID="tefap-complete-now-button"
        />
      </View>
    );
    extraBodyContent = (
      <View style={styles.tefapContainer}>
        <Text style={styles.tefapText}>{translation.tefap_attestation_needed}</Text>
        <Text style={[styles.tefapText, styles.boldText]}>{translation.tefap_attestation_complete_now}</Text>
        <Text style={[styles.tefapText, styles.tefapDescriptionText]}>{translation.tefap_description}</Text>
        <TouchableOpacity onPress={openTefapLink}>
          <Text style={styles.tefapInfoLink}>{translation.tefap_what_is_tefap}</Text>
        </TouchableOpacity>
      </View>
    );
  } else if (hasMissingInfo) {
    buttonRowContent.push(
      <View style={styles.buttonContainer} key="missing_later">
        <FilledButton
          onPress={onCancel}
          label={translation.program_complete_later}
          full={false}
          color={Colors.darkGreen}
          paddingHorizontal={16}
        />
      </View>
    );
    buttonRowContent.push(
      <View style={styles.buttonContainer} key="missing_now">
        <FilledButton
          onPress={() => handleReservationCompleteFlow(() => showMissingInfoModal())}
          label={translation.program_complete_now}
          full={true}
          color={Colors.darkGreen}
          paddingHorizontal={8}
          testID="missing-info-complete-now-button"
        />
      </View>
    );
    extraBodyContent = (
      <View style={styles.tefapContainer}>
        <Text style={styles.tefapText}>{translation.tefap_pantry_requests_info}</Text>
      </View>
    );
  } else {
    buttonRowContent.push(
      <View style={styles.buttonContainer} key="close">
        <FilledButton
          onPress={onCancel}
          label={translation.app_button_close}
          full={true}
          color={Colors.darkGreen}
          paddingHorizontal={8}
        />
      </View>
    );
  }

  const headerText =
    visit.service_type === ServiceType.pre_registration
      ? translation.pre_registration_confirmed
      : translation.reservation_confirmed;
  const subTitle =
    visit.service_type === ServiceType.pre_registration
      ? translation.your_upcoming_pre_registration
      : translation.your_upcoming_reservation;

  const time =
    visit.service_type === ServiceType.pre_registration
      ? `${niceDate}, ${niceTime} - ${niceUntilTime}`
      : `${niceDate}, ${niceTime}`;

  return (
    <View style={styles.container}>
      <Modal
        isVisible={true}
        deviceWidth={Layout.fullWidth}
        deviceHeight={Layout.fullHeight}
        style={styles.modal}
        onBackdropPress={onCancel}
      >
        <View style={styles.contentContainer}>
          <View style={styles.body}>
            <Image
              source={require('../../assets/images/plentiful_fruits.png')}
              style={styles.modalImage}
              resizeMode="contain"
            />
            <Text style={styles.headerText}>{headerText}</Text>
            <Text style={[styles.subTitle, styles.boldText]}>{subTitle}:</Text>
            <Text>{time}</Text>
            {extraBodyContent}
          </View>
          <View style={styles.buttonRow}>{buttonRowContent}</View>
        </View>
      </Modal>
    </View>
  );
}

export default ReservationConfirmedModal;
